/**
 * Auto Ecotaxe
 * - Déclencheur : propriété _requireGravage sur les lignes panier (posée par auto-gravage.js).
 * - Montant déterminé automatiquement selon le prix unitaire final :
 *   < 1000€ HT → 10,64€ (ecotaxe_1064) | ≥ 1000€ HT → 27,54€ (ecotaxe_2754)
 */
(() => {
  if (window.__autoEcotaxeInitialized) return;
  window.__autoEcotaxeInitialized = true;

  // Variant IDs des produits fantômes d'écotaxe
  const ECOTAXE_VARIANT_IDS = {
    ecotaxe_1064: 57673999974725, // Éco-participation vélo <1000€ HT — 10,64€
    ecotaxe_2754: 57674490642757, // Éco-participation vélo >1000€ HT — 27,54€
  };

  // Seuil en centimes (100 000 = 1000,00€)
  const ECOTAXE_PRICE_THRESHOLD = 100000;
  const REQUIRE_GRAVAGE_PROPERTY = '_requireGravage';
  const ECOTAXE_VARIANT_ID_SET = new Set(Object.values(ECOTAXE_VARIANT_IDS).map(Number));
  // Même ID que GRAVAGE_VARIANT_ID dans auto-gravage.js
  const GRAVAGE_VARIANT_ID = 57195351540037;
  const ECOTAXE_PROPERTY = '_isEcotaxe';
  const CART_UPDATE_EVENT = 'cart:update';
  const ADD_ENDPOINT = '/cart/add.js';
  const CART_ENDPOINT = '/cart.js';
  const CHANGE_ENDPOINT = '/cart/change.js';
  const SYNC_DEBOUNCE_MS = 200;
  const IS_CART_PAGE = window.location.pathname.startsWith('/cart');

  let isSyncing = false;
  let syncTimer = null;

  const fetchCart = async () => {
    const response = await fetch(CART_ENDPOINT, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Failed to fetch cart');
    return response.json();
  };

  const addItem = async (body, isJson = false) => {
    const headers = { Accept: 'application/json' };
    if (isJson) headers['Content-Type'] = 'application/json';
    const response = await fetch(ADD_ENDPOINT, { method: 'POST', headers, body });
    const data = await response.json();
    if (!response.ok || data?.status) throw new Error(data?.message || 'Failed to add to cart');
    return data;
  };

  const changeLineItemQuantity = async (lineItemKey, quantity) => {
    const response = await fetch(CHANGE_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: lineItemKey, quantity }),
    });
    const data = await response.json();
    if (!response.ok) throw new Error('Failed to change cart item');
    return data;
  };

  const dispatchCartUpdate = (cart) => {
    document.dispatchEvent(new CustomEvent(CART_UPDATE_EVENT, {
      bubbles: true,
      detail: { resource: cart, sourceId: 'auto-ecotaxe', data: { source: 'auto-ecotaxe', itemCount: cart?.item_count ?? 0 } },
    }));
  };

  const countNeededEcotaxe = (cart) => {
    const needed = { ecotaxe_1064: 0, ecotaxe_2754: 0 };
    if (!cart?.items) return needed;
    for (const item of cart.items) {
      // Ignorer les lignes écotaxe et gravage, même si elles ont reçu les propriétés d'un vélo
      if (ECOTAXE_VARIANT_ID_SET.has(Number(item.variant_id))) continue;
      if (Number(item.variant_id) === GRAVAGE_VARIANT_ID) continue;
      // Uniquement les vélos (propriété _requireGravage posée par auto-gravage.js)
      if (!item.properties?.[REQUIRE_GRAVAGE_PROPERTY]) continue;
      // Type selon le prix unitaire final (final_price est déjà un prix unitaire)
      const type = item.final_price >= ECOTAXE_PRICE_THRESHOLD ? 'ecotaxe_2754' : 'ecotaxe_1064';
      needed[type] += item.quantity;
    }
    return needed;
  };

  const isCleanEcotaxeItem = (item) =>
    Object.keys(item.properties || {}).every((key) => key === ECOTAXE_PROPERTY);

  const findEcotaxeItems = (cart, variantId) =>
    cart?.items?.filter((item) => Number(item.variant_id) === Number(variantId)) ?? [];

  const syncEcotaxeFromCart = async () => {
    if (isSyncing) return;
    isSyncing = true;

    try {
      const cart = await fetchCart();
      let updatedCart = cart;
      let changed = false;

      const needed = countNeededEcotaxe(cart);

      for (const [type, variantId] of Object.entries(ECOTAXE_VARIANT_IDS)) {
        const requiredQty = needed[type] ?? 0;
        const ecotaxeItems = findEcotaxeItems(updatedCart, variantId);

        // Ligne écotaxe qui a reçu les propriétés d'un vélo : on la remplace par une ligne propre
        for (const item of ecotaxeItems.filter((item) => !isCleanEcotaxeItem(item))) {
          updatedCart = await changeLineItemQuantity(item.key, 0);
          changed = true;
        }
        const existingItem = ecotaxeItems.find(isCleanEcotaxeItem);

        if (requiredQty <= 0) {
          if (existingItem) { updatedCart = await changeLineItemQuantity(existingItem.key, 0); changed = true; }
        } else if (existingItem) {
          if (existingItem.quantity !== requiredQty) {
            updatedCart = await changeLineItemQuantity(existingItem.key, requiredQty);
            changed = true;
          }
        } else {
          await addItem(JSON.stringify({ id: variantId, quantity: requiredQty, properties: { [ECOTAXE_PROPERTY]: '1' } }), true);
          updatedCart = await fetchCart();
          changed = true;
        }
      }

      if (changed) dispatchCartUpdate(updatedCart);
    } catch (error) {
      console.error('[auto-ecotaxe] sync failed', error);
    } finally {
      isSyncing = false;
    }
  };

  const scheduleSync = () => {
    if (syncTimer) window.clearTimeout(syncTimer);
    syncTimer = window.setTimeout(syncEcotaxeFromCart, SYNC_DEBOUNCE_MS);
  };

  const handleCartUpdate = (event) => {
    if (event instanceof CustomEvent && event?.detail?.data?.source === 'auto-ecotaxe') return;
    scheduleSync();
  };

  document.addEventListener(CART_UPDATE_EVENT, handleCartUpdate);

  if (IS_CART_PAGE) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', scheduleSync, { once: true });
    } else {
      scheduleSync();
    }
  }
})();
